import { useEffect } from 'react';
import { useSetState } from 'minimal-shared/hooks';
import Button from '@mui/material/Button';
import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';
import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';
import { getData, increment, deleteData, updateData, createBatch, serverTimestamp, distributedCounter, sendPushNotification } from 'src/lib/firebase';
import { Iconify } from 'src/components/iconify';
import { useAuthContext, useRedirectToSignIn } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export function ButtonFollow({
  userId,
  sx,
  ...other
}) {
  const router = useRouter();
  const {
    user
  } = useAuthContext();
  const {
    t,
    currentLang
  } = useTranslate();
  const urlToSignIn = useRedirectToSignIn();
  const {
    state,
    setState
  } = useSetState({
    loading: false,
    following: false
  });
  const fetchFollowing = async () => {
    setState({
      loading: true
    });
    const userFollowed = await getData({
      path: `users/${user?.id}/following/${userId}`
    });
    setState({
      loading: false,
      following: !!userFollowed?.id
    });
  };
  useEffect(() => {
    if (user?.id) fetchFollowing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  if (user?.id === userId) return null;
  const sendNotification = async ({
    id,
    country,
    photoURL = '',
    displayName = ''
  }, createdAt, batch) => {
    const tokens = await getData({
      path: 'notificationsTokens',
      defaultData: [],
      options: {
        where: [{
          field: 'uid',
          operator: '==',
          value: userId
        }]
      }
    });
    const newFollowerAllowed = tokens.some(({
      allowed
    }) => allowed.includes('activity_new_follower'));
    if (!newFollowerAllowed) {
      await batch.commit();
      return;
    }
    const message = t('has started following you');
    const action = `${CONFIG.appDomain}/profile/${id}${newSearchParams({
      country
    })}`;
    updateData(`notifications/${userId}`, {
      totalUnRead: increment(1),
      totalNotifications: increment(1)
    }, batch);
    updateData(`notifications/${userId}/messages`, {
      action,
      createdAt,
      isUnRead: true,
      type: 'social',
      avatarUrl: photoURL,
      category: 'Social network',
      title: `<p><strong>${displayName}</strong> ${message}</p>`
    }, batch);
    await batch.commit();
    sendPushNotification({
      tokens,
      message,
      link: action,
      title: displayName
    });
  };
  const handleClick = async () => {
    if (!user?.id) {
      router.push(urlToSignIn);
      return;
    }
    if (!user?.profileCompleted) {
      router.push(`${paths.user.account}${newSearchParams({
        country: currentLang.countryName
      })}`);
      return;
    }
    if (!state.loading) {
      const following = !state.following;
      setState({
        following,
        loading: true
      });
      const batch = createBatch();
      const followedAt = serverTimestamp();
      distributedCounter(`users/${userId}`, `totalFollowers`, following ? 1 : -1, batch);
      distributedCounter(`users/${user?.id}`, `totalFollowing`, following ? 1 : -1, batch);
      if (following) {
        updateData(`users/${userId}/followers/${user?.id}`, {
          followedAt
        }, batch);
        updateData(`users/${user?.id}/following/${userId}`, {
          followedAt
        }, batch);
        await sendNotification(user, followedAt, batch);
      } else {
        deleteData(`users/${userId}/followers/${user?.id}`, batch);
        deleteData(`users/${user?.id}/following/${userId}`, batch);
        await batch.commit();
      }
      setState({
        loading: false
      });
      router.refresh();
    }
  };
  return <Button size="small" onClick={handleClick} disabled={state.loading} variant={state.following ? 'text' : 'outlined'} color={state.following ? 'success' : 'inherit'} startIcon={state.following && <Iconify width={18} icon="eva:checkmark-fill" sx={{
    mr: -0.5
  }} />} sx={[{
    flexShrink: 0,
    px: {
      xs: 1,
      md: 1.5
    }
  }, ...(Array.isArray(sx) ? sx : [sx])]} {...other}>
      {state.following ? t('Following') : t('Follow')}
    </Button>;
}